// Prompt 0470 M8 — Orquestador del panel de Tipo de Rol de Gobernanza.
// Recibe `tipo: TipoRolGobernanzaAdmin | null` (catalogo por tenant).
// 4 tabs: Datos, Asignaciones, Ficha FG, Auditoria.

import { useState, useEffect } from 'react';
import { useI18n } from '../../../../../../i18n';
import type { TipoRolGobernanzaAdmin } from '../../../../../shared/services/api';
import { DatosTab } from './tabs/DatosTab';
import { AsignacionesTab } from './tabs/AsignacionesTab';
import { FichaTab } from './tabs/FichaTab';
import { AuditoriaTab } from './tabs/AuditoriaTab';
import { DirtyStateWarning } from '../ficha-shared/DirtyStateWarning';
import { useDirtyTabs } from '../ficha-shared/useDirtyTabs';
import { AdminOverlayPanel } from '../shared/AdminOverlayPanel';

export type RolGobernanzaTabId = 'datos' | 'asignaciones' | 'ficha' | 'auditoria';

interface AdminRolGobernanzaPanelProps {
  tipo: TipoRolGobernanzaAdmin | null;
  initialTab?: RolGobernanzaTabId;
  onClose: () => void;
  onSaved: () => void;
}

export function AdminRolGobernanzaPanel({
  tipo,
  initialTab = 'datos',
  onClose,
  onSaved,
}: AdminRolGobernanzaPanelProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const panel = ((adm ?? {}) as Record<string, unknown>).tipoRolGobernanzaPanel as
    | Record<string, string>
    | undefined;
  const lbl = panel ?? {};

  const [currentTipo, setCurrentTipo] = useState<TipoRolGobernanzaAdmin | null>(tipo);
  const [activeTab, setActiveTab] = useState<RolGobernanzaTabId>(initialTab);
  const [showDirtyWarning, setShowDirtyWarning] = useState(false);

  const dirty = useDirtyTabs<RolGobernanzaTabId>();

  useEffect(() => {
    setCurrentTipo(tipo);
  }, [tipo]);

  // Escape gestionado por AdminOverlayPanel (desde 0483). Guard de dirty-state
  // aplicado via requestClose que se pasa como onClose al overlay.

  const TABS: Array<{
    key: RolGobernanzaTabId;
    label: string;
    disabled?: boolean;
    reason?: string;
  }> = [
    { key: 'datos', label: lbl.tabDatos ?? 'Datos' },
    {
      key: 'asignaciones',
      label: lbl.tabAsignaciones ?? 'Asignaciones',
      disabled: !currentTipo,
      reason: lbl.asignacionesDeshabilitada ?? 'Guarda el tipo primero',
    },
    {
      key: 'ficha',
      label: lbl.tabFicha ?? 'Ficha FG',
      disabled: !currentTipo,
      reason: lbl.fichaDeshabilitada ?? 'Guarda el tipo primero',
    },
    {
      key: 'auditoria',
      label: lbl.tabAuditoria ?? 'Auditoria',
      disabled: !currentTipo,
      reason: lbl.auditoriaDeshabilitada ?? 'Guarda el tipo primero',
    },
  ];

  function requestClose() {
    if (dirty.anyDirty()) setShowDirtyWarning(true);
    else onClose();
  }

  function confirmDiscardAndClose() {
    dirty.markAllClean();
    setShowDirtyWarning(false);
    onClose();
  }

  function tabLabel(id: RolGobernanzaTabId): string {
    return TABS.find((x) => x.key === id)?.label ?? id;
  }

  const tituloMostrado = currentTipo?.nombre ?? lbl.nuevoTipo ?? 'Nuevo tipo de rol';

  return (
    <AdminOverlayPanel open={true} onClose={requestClose} maxWidth={1000}>
      <>
        <div
          style={{
            flexShrink: 0,
            padding: '24px 32px 0',
            borderBottom: '1px solid #E2E6ED',
            background: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1F3864' }}>
              {tituloMostrado}
              {currentTipo?.esSistema && (
                <span
                  style={{
                    marginLeft: 10,
                    padding: '3px 10px',
                    background: '#EEF2FF',
                    color: '#4338CA',
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: 4,
                    letterSpacing: 0.4,
                    verticalAlign: 'middle',
                  }}
                >
                  {lbl.badgeSistema ?? 'SISTEMA'}
                </span>
              )}
            </h2>
          </div>

          <div style={{ display: 'flex' }}>
            {TABS.map((tab) => {
              const active = activeTab === tab.key;
              const tabIsDirty = dirty.isDirty(tab.key);
              return (
                <button
                  key={tab.key}
                  disabled={tab.disabled}
                  title={tab.disabled ? tab.reason : undefined}
                  onClick={() => !tab.disabled && setActiveTab(tab.key)}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: 'transparent',
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    color: tab.disabled ? '#C4C9D1' : active ? '#1F3864' : '#5A6171',
                    borderBottom: active ? '2px solid #1F3864' : '2px solid transparent',
                    cursor: tab.disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    marginBottom: -1,
                  }}
                >
                  {tab.label}
                  {tabIsDirty && (
                    <span
                      style={{ marginLeft: 6, color: '#DC2626', fontSize: 18, lineHeight: 1 }}
                      aria-label="unsaved"
                    >
                      ·
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px 32px 24px' }}>
          {activeTab === 'datos' && (
            <DatosTab
              tipo={currentTipo}
              isDirty={dirty.isDirty('datos')}
              onMarkDirty={() => dirty.markDirty('datos')}
              onMarkClean={() => dirty.markClean('datos')}
              onSaved={async (saved) => {
                dirty.markClean('datos');
                setCurrentTipo(saved);
                onSaved();
              }}
            />
          )}

          {activeTab === 'asignaciones' && currentTipo && (
            <AsignacionesTab tipo={currentTipo} onChanged={onSaved} />
          )}

          {activeTab === 'ficha' && currentTipo && (
            <FichaTab
              tipoRolId={currentTipo.id}
              isDirty={dirty.isDirty('ficha')}
              onMarkDirty={() => dirty.markDirty('ficha')}
              onMarkClean={() => dirty.markClean('ficha')}
            />
          )}

          {activeTab === 'auditoria' && currentTipo && <AuditoriaTab tipoRolId={currentTipo.id} />}
        </div>

        <DirtyStateWarning
          open={showDirtyWarning}
          tabsDirtyLabels={dirty.dirtyTabIds().map(tabLabel)}
          onConfirm={confirmDiscardAndClose}
          onCancel={() => setShowDirtyWarning(false)}
        />
      </>
    </AdminOverlayPanel>
  );
}
